import React, { useRef, useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { Checkbox } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { decoratedFetch } from "../req_utils";
import ThemedTypography from "./ThemedTypography";
import ThemedIconButton from "./ThemedIconButton";
import ThemedDataGridPro from "./ThemedDataGridPro";
import ThemedFormControlLabel from "./ThemedFormControlLabel";
import ThemedTextField from "./ThemedTextField";
import FieldWorkOrderDialog from "./dialogs/FieldWorkOrderDialog";
import FilePreview from "./FilePreview";
import { refreshFiles } from "../reducers";

// Predefined exclusion options for Field Work Order
const FIELD_WORK_ORDER_EXCLUSIONS = [
  "DEMOLITION",
  "FURN / EQUIP MOVING",
  "FLOOR PATCH / REPAIR",
  "MOISTURE TESTING",
  "MOISTURE TREATMENT",
  "BORDERS, INLAYS, OR PATTERNS",
  "NON-STANDARD WORK HOURS",
  "OTHER",
];

const FieldWorkOrder = ({ open }) => {
  const dispatchState = useDispatch();
  const proposal = useSelector((state) => state.selectedProposal);
  const proposalId = useSelector((state) => state.selectedProposal?.id);
  const project = useSelector((state) => state.project);
  const [selectedInclusions, setSelectedInclusions] = useState([]);
  // Track checked state for predefined exclusions
  const [exclusionCheckboxes, setExclusionCheckboxes] = useState({});
  const [includedArea, setIncludedArea] = useState("");
  const [installDate, setInstallDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [scopeOfWorkNotes, setScopeOfWorkNotes] = useState("");
  const [footerNotes, setFooterNotes] = useState("");
  const [fwoInclusions, setFwoInclusions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFileLink, setPreviewFileLink] = useState("");
  const inclusionsApiRef = useGridApiRef();
  const enterPressedRef = useRef(false);

  // Filter inclusions to only show Labor items
  const laborInclusions = fwoInclusions;
  const normalizeFwoInclusions = (inclusions = []) =>
    inclusions.map((incl, index) => ({
      ...incl,
      api_id: incl.id || incl.api_id || `fwo_${index}`,
      id: index,
      category: "Labor",
    }));

  const saveFWO = (inclusionsOverride = null) => {
    if (!proposal || !proposal.id) {
      setSnackbarMessage("Proposal ID is missing");
      return;
    }

    // Convert FWO exclusions to the format expected by the backend
    const exclusions = FIELD_WORK_ORDER_EXCLUSIONS.map((exclusion, index) => ({
      description: exclusion,
      checked: exclusionCheckboxes[exclusion] === true,
    }));

    // Convert FWO inclusions from labor inclusions
    const sourceInclusions = inclusionsOverride || laborInclusions;
    const inclusions = sourceInclusions.map((incl) => ({
      description: incl.description || "",
    }));

    decoratedFetch(`/create_or_update_fwo`, {
      method: "POST",
      body: JSON.stringify({
        proposal_id: proposal.id,
        included_area: includedArea,
        install_date: installDate,
        start_time: startTime,
        scope_of_work_notes: scopeOfWorkNotes,
        footer_notes: footerNotes,
        inclusions: inclusions,
        exclusions: exclusions,
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error message generated");
        }
        return response.json();
      })
      .then((data) => {
        // Update local state with returned FWO data
        if (data.included_area !== undefined) setIncludedArea(data.included_area || "");
        if (data.install_date !== undefined) setInstallDate(data.install_date || "");
        if (data.start_time !== undefined) setStartTime(data.start_time || "");
        if (data.scope_of_work_notes !== undefined) setScopeOfWorkNotes(data.scope_of_work_notes || "");
        if (data.footer_notes !== undefined) setFooterNotes(data.footer_notes || "");
        if (data.inclusions) setFwoInclusions(normalizeFwoInclusions(data.inclusions));
      })
      .catch((error) => {
        setSnackbarMessage("Failed to save Field Work Order");
        console.error(`Error saving FWO: ${error}`);
      });
  };

  const descriptionColumns = [
    {
      field: "description",
      headerName: "Description",
      editable: true,
      flex: 1,
    },
  ];

  const handleCellKeyDown = (params, event) => {
    if (event.key === "Enter") {
      enterPressedRef.current = true;
      return;
    }
    enterPressedRef.current = false;
  };

  const importProposalLabor = () => {
    decoratedFetch("/import_proposal_labor_to_fwo", {
      method: "POST",
      body: JSON.stringify({
        proposal_id: proposal.id,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.length) {
          const nextRows = normalizeFwoInclusions(data);
          setFwoInclusions(nextRows);
          saveFWO(nextRows);
          return;
        }
        setFwoInclusions([]);
        saveFWO([]);
      })
      .catch((error) => {
        console.error(`Error importing labor to field work order: ${error}`);
      });
  };

  const handleAddInclusionRow = () => {
    const newRow = {
      id: fwoInclusions.length,
      api_id: "temp_id",
      quantity: "",
      unit: "",
      description: "",
      unit_price: "",
      extension: "",
      category: "Labor",
    };
    const nextRows = [...fwoInclusions, newRow];
    setFwoInclusions(nextRows);
    saveFWO(nextRows);
  };

  const handleInclusionRowDelete = () => {
    const newRows = fwoInclusions
      .filter((row) => !selectedInclusions.includes(row.id))
      .map((incl, i) => ({ ...incl, id: i }));
    setFwoInclusions(newRows);
    saveFWO(newRows);
    setSelectedInclusions([]);
  };

  const handleInclusionEditCommit = (updatedRow) => {
    if (!updatedRow.description) {
      const newRows = fwoInclusions
        .filter((incl) => incl.id !== updatedRow.id)
        .map((incl, i) => ({ ...incl, id: i }));
      setFwoInclusions(newRows);
      saveFWO(newRows);
      enterPressedRef.current = false;
      return updatedRow;
    }
    const newRows = fwoInclusions.map((row) => {
      if (row.id === updatedRow.id) {
        const newRow = {
          ...row,
          ...updatedRow,
          api_id: row.api_id || updatedRow.api_id || `row_${row.id}`,
          category: "Labor",
        };
        return newRow;
      }
      return row;
    });
    if (
      enterPressedRef.current &&
      updatedRow.id === fwoInclusions[fwoInclusions.length - 1].id
    ) {
      const newRow = {
        id: fwoInclusions.length,
        api_id: "temp_id",
        quantity: "",
        unit: "",
        description: "",
        unit_price: "",
        extension: "",
        category: "Labor",
      };
      newRows.push(newRow);
    }
    enterPressedRef.current = false;
    setFwoInclusions(newRows);
    saveFWO(newRows);
    return updatedRow;
  };

  const handleInclusionRowSelectionModelChange = useCallback(
    (newSelectionModel) => {
      setSelectedInclusions(newSelectionModel);
    },
    [setSelectedInclusions]
  );

  const handleExclusionCheckboxChange = (exclusion, checked) => {
    const newCheckedState = {
      ...exclusionCheckboxes,
      [exclusion]: checked,
    };
    setExclusionCheckboxes(newCheckedState);
    
    // Save FWO with updated exclusions
    saveFWO();
  };


  const getFWO = () => {
    if (!proposalId) return;
    
    decoratedFetch(`/get_fwo/${proposalId}`)
      .then((response) => {
        if (response.status === 404) {
          // FWO doesn't exist yet, initialize with empty values
          setIncludedArea("");
          setInstallDate("");
          setStartTime("");
          setScopeOfWorkNotes("");
          setFooterNotes("");
          setFwoInclusions([]);
          setExclusionCheckboxes({});
          return;
        }
        if (response.status !== 200) {
          throw new Error("Error message generated");
        }
        return response.json();
      })
      .then((data) => {
        if (!data) return;
        
        // Load FWO data into state
        setIncludedArea(data.included_area || "");
        setInstallDate(data.install_date || "");
        setStartTime(data.start_time || "");
        setScopeOfWorkNotes(data.scope_of_work_notes || "");
        setFooterNotes(data.footer_notes || "");
        const fwoRows = normalizeFwoInclusions(data.inclusions || []);
        setFwoInclusions(fwoRows);
        
        // Initialize exclusion checkboxes from FWO exclusions
        const checkedState = {};
        FIELD_WORK_ORDER_EXCLUSIONS.forEach((exclusion) => {
          const found = (data.exclusions || []).find(
            (exc) => exc.description?.toUpperCase() === exclusion && exc.checked === true
          );
          checkedState[exclusion] = !!found;
        });
        setExclusionCheckboxes(checkedState);
      })
      .catch((error) => {
        console.error(`Error fetching FWO: ${error}`);
        // Initialize with empty values on error
        setIncludedArea("");
        setInstallDate("");
        setStartTime("");
        setScopeOfWorkNotes("");
        setFooterNotes("");
        setFwoInclusions([]);
        setExclusionCheckboxes({});
      });
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    if (proposalId) {
      getFWO();
    }
  }, [open, proposalId]);

  if (!proposalId) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ pr: "10px", mb: "12px" }}>
          <ThemedTextField
            label="Included Area"
            variant="outlined"
            fullWidth
            size="small"
            value={includedArea}
            onChange={(e) => setIncludedArea(e.target.value)}
            onBlur={saveFWO}
            InputLabelProps={{ shrink: !!includedArea }}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ pr: "10px", mb: "12px" }}>
          <ThemedTextField
            label="Install Date"
            variant="outlined"
            fullWidth
            size="small"
            value={installDate}
            onChange={(e) => {
              setInstallDate(e.target.value);
            }}
            onBlur={saveFWO}
            placeholder="e.g., Fri. 11/5 thru Mon. 11/8"
            InputLabelProps={{ shrink: !!installDate }}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ pr: "10px", mb: "12px" }}>
          <ThemedTextField
            label="Start Time"
            variant="outlined"
            fullWidth
            size="small"
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
            }}
            onBlur={saveFWO}
            placeholder="e.g., 8:00 AM"
            InputLabelProps={{ shrink: !!startTime }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ pr: "10px", display: "flex", justifyContent: "space-around" }}
        >
          <Button
            size="small"
            onClick={importProposalLabor}
            fullWidth
            sx={{ pt: "8px" }}
          >
            Import Labor
          </Button>
          <Button
            size="small"
            onClick={() => setOpenDialog(true)}
            fullWidth
            sx={{ pt: "8px" }}
          >
            Generate Field Work Order PDF
          </Button>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            pr: "10px",
            mt: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <ThemedTypography sx={{ fontSize: "1.2rem" }}>
            Inclusions
          </ThemedTypography>
          <Grid container justifyContent="flex-end">
            <Grid
              item
              sx={{
                marginRight: "4px",
                visibility: selectedInclusions.length ? "visible" : "hidden",
              }}
            >
              {loading ? (
                <CircularProgress />
              ) : (
                <ThemedIconButton onClick={handleInclusionRowDelete}>
                  <RemoveIcon />
                </ThemedIconButton>
              )}
            </Grid>
            <Grid item>
              <ThemedIconButton onClick={handleAddInclusionRow}>
                <AddIcon />
              </ThemedIconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ pr: "10px", mb: "11px", width: "100%" }}>
          {laborInclusions?.length ? (
            <ThemedDataGridPro
              apiRef={inclusionsApiRef}
              onRowCountChange={() => {
                if (
                  laborInclusions?.length &&
                  laborInclusions[laborInclusions.length - 1].api_id ===
                    "temp_id"
                ) {
                  inclusionsApiRef.current.setCellFocus(
                    laborInclusions.length - 1,
                    "description"
                  );
                  inclusionsApiRef.current.startCellEditMode({
                    id: laborInclusions.length - 1,
                    field: "description",
                  });
                }
              }}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  display: "none",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cellCheckbox:focus, & .MuiDataGrid-cellCheckbox:focus-within":
                  {
                    outline: "none",
                  },
              }}
              autoHeight
              hideFooter
              rows={laborInclusions}
              columns={descriptionColumns}
              processRowUpdate={handleInclusionEditCommit}
              onProcessRowUpdateError={(error) => console.error(error)}
              experimentalFeatures={{ newEditingApi: true }}
              checkboxSelection
              onRowSelectionModelChange={(newSelectionModel) =>
                handleInclusionRowSelectionModelChange(newSelectionModel)
              }
              rowSelectionModel={selectedInclusions}
              getRowId={(row) => row.id}
              onCellClick={(params, e) => e.stopPropagation()}
              localeText={{ noRowsLabel: "No Inclusions" }}
              onCellKeyDown={handleCellKeyDown}
              rowReordering
              onRowOrderChange={(e) => {
                const mutableInclusions = [...laborInclusions];
                const sourceRow = mutableInclusions[e.oldIndex];
                mutableInclusions.splice(e.oldIndex, 1);
                mutableInclusions.splice(e.targetIndex, 0, sourceRow);
                const nextRows = mutableInclusions.map((incl, i) => ({
                  ...incl,
                  id: i,
                }));
                setFwoInclusions(nextRows);
                saveFWO(nextRows);
              }}
            />
          ) : null}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            pr: "10px",
            mt: "10px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ThemedTypography sx={{ fontSize: "1.2rem" }}>
            Excluded From Our Work
          </ThemedTypography>
        </Grid>
        <Grid item xs={12} sx={{ pr: "10px", mb: "11px", width: "100%" }}>
          <Grid container spacing={2}>
            {FIELD_WORK_ORDER_EXCLUSIONS.map((exclusion) => (
              <Grid item xs={12} sm={6} md={3} key={exclusion}>
                <ThemedFormControlLabel
                  control={
                    <Checkbox
                      checked={exclusionCheckboxes[exclusion] || false}
                      onChange={(e) =>
                        handleExclusionCheckboxChange(exclusion, e.target.checked)
                      }
                    />
                  }
                  label={exclusion}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ pr: "10px", mb: "12px" }}>
          <ThemedTextField
            label="Scope of Work Notes"
            variant="outlined"
            fullWidth
            size="small"
            multiline
            rows={4}
            value={scopeOfWorkNotes}
            onChange={(e) => {
              setScopeOfWorkNotes(e.target.value);
            }}
            onBlur={saveFWO}
            placeholder="Enter notes for the Scope of Work section..."
            InputLabelProps={{ shrink: !!scopeOfWorkNotes }}
          />
        </Grid>
        <Grid item xs={12} sx={{ pr: "10px", mb: "12px" }}>
          <ThemedTextField
            label="Footer Notes"
            variant="outlined"
            fullWidth
            size="small"
            multiline
            rows={4}
            value={footerNotes}
            onChange={(e) => {
              setFooterNotes(e.target.value);
            }}
            onBlur={saveFWO}
            placeholder="Enter notes for the footer section..."
            InputLabelProps={{ shrink: !!footerNotes }}
          />
        </Grid>
      </Grid>
        <FieldWorkOrderDialog
        open={openDialog}
        handleClose={(success, newFile, presignedUrl) => {
          setOpenDialog(false);
          if (success) {
            getFWO();
            dispatchState(refreshFiles(true));
            if (newFile && presignedUrl) {
              setPreviewFile(newFile);
              setPreviewFileLink(presignedUrl);
            }
          }
        }}
        proposal={proposal}
        project={project}
        exclusionCheckboxes={exclusionCheckboxes}
        installDate={installDate}
        startTime={startTime}
        includedArea={includedArea}
        fwoInclusions={fwoInclusions}
        scopeOfWorkNotes={scopeOfWorkNotes}
        footerNotes={footerNotes}
      />
      {previewFileLink && previewFile && (
        <FilePreview
          handleClose={() => {
            setPreviewFile(null);
            setPreviewFileLink("");
          }}
          url={previewFileLink}
          file={previewFile}
        />
      )}
      <Snackbar
        open={snackbarMessage !== ""}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
    </>
  );
};

export default FieldWorkOrder;

